class ShippingBar extends HTMLElement {
  constructor() {
    super();
    this.currentCurrency = Shopify.currency.active;
    this.conversionRate = Shopify.currency.rate;
    this.threshold = this.dataset.threshold;
    this.convertedThreshold = 0;
    this.totalPrice = this.dataset.totalPrice;
    this.moneyFormat = this.dataset.moneyFormat;
    this.empty = this.dataset.empty === 'true';
  }

  connectedCallback() {
    this.threshold = this.getConvertedPrice(this.threshold);
    if (this.empty) {
      this.emptyText = this.querySelector('[data-empty-text]');
      this.showEmptyText();
      return;
    }

    this.progressText = this.querySelector('[data-progress-text]');
    this.progressBar = this.querySelector('[data-progress-bar]');
    this.progressItems = this.querySelectorAll('[data-progress-item]');

    this.setActiveItems();

    const thresholdIsPassed = Number(this.totalPrice) >= Number(this.threshold);
    if (!thresholdIsPassed && Number(this.threshold) > 0) {
      this.showProgress();
    }

    this.updateProgressBar();
    this.observeResize();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  showEmptyText() {
    this.emptyText.innerHTML = this.emptyText.innerHTML.replace(
      '[amount]',
      this.getFormattedPrice(this.threshold),
    );
    this.emptyText.classList.remove('hidden');
  }

  getConvertedPrice(price) {
    return (price * (this.conversionRate || 1)).toFixed(0);
  }

  showProgress() {
    const progressText = this.progressText.innerHTML.replace(
      '[amount]',
      this.getFormattedPrice(this.threshold - this.totalPrice),
    );

    this.progressText.innerHTML = progressText;
    this.progressText.classList.remove('hidden');
  }

  observeResize() {
    if (typeof ResizeObserver === 'undefined') return;

    // Recompute whenever the track resizes — covers the drawer opening
    // (0 → real width) and viewport resizes.
    this.resizeObserver = new ResizeObserver(() => this.updateProgressBar());
    this.resizeObserver.observe(this.progressBar.parentElement);
  }

  updateProgressBar() {
    // The track must be visible before its width can be measured.
    this.progressBar.parentElement.classList.remove('hidden');

    const percent = this.getAlignedPercent(Number(this.totalPrice));
    if (percent === null) return; // Not measurable yet; observer will retry.

    this.progressBar.style.width = `${percent}%`;
  }

  // Maps the cart total to a fill percentage that lands exactly on each
  // checkpoint dot's real horizontal position, interpolating linearly
  // between adjacent checkpoints.
  getAlignedPercent(total) {
    const track = this.progressBar.parentElement;
    const trackRect = track.getBoundingClientRect();
    const trackWidth = trackRect.width;

    if (!trackWidth) return null;

    const stops = Array.from(this.progressItems)
      .map((item) => {
        const dot = item.querySelector('.circle-dot');
        const dotRect = dot.getBoundingClientRect();
        const center = dotRect.left + dotRect.width / 2 - trackRect.left;
        const pos = Math.max(0, Math.min(100, (center / trackWidth) * 100));

        return {
          price: Number(this.getConvertedPrice(Number(item.dataset.threshold))),
          pos,
        };
      })
      .sort((a, b) => a.price - b.price);

    const points = [{ price: 0, pos: 0 }, ...stops];
    const last = points[points.length - 1];

    if (total <= 0) return 0;
    if (total >= last.price) return 100; // Everything unlocked — fill the track.

    for (let i = 1; i < points.length; i += 1) {
      if (total <= points[i].price) {
        const prev = points[i - 1];
        const next = points[i];
        const span = next.price - prev.price;
        const ratio = span === 0 ? 0 : (total - prev.price) / span;

        return prev.pos + ratio * (next.pos - prev.pos);
      }
    }

    return last.pos;
  }

  setActiveItems() {
    this.progressItems.forEach((progressItem) => {
      const priceEl = progressItem.querySelector('[data-progress-item-price]');
      const convertedPrice = this.getConvertedPrice(
        +progressItem.dataset.threshold,
      );
      const remainingPrice = convertedPrice - +this.totalPrice;
      const isActive = remainingPrice <= 0;

      priceEl.textContent = isActive
        ? `${this.getFormattedPrice(convertedPrice)}`
        : `+ ${this.getFormattedPrice(remainingPrice)}`;
      progressItem.classList.toggle('is-active', isActive);

      if (isActive)
        this.progressText.innerHTML = progressItem.dataset.description;
    });
  }

  getFormattedPrice(price) {
    const formattedPrice = Shopify.formatMoney(price, this.moneyFormat);

    return formattedPrice;
  }
}

customElements.define('shipping-bar', ShippingBar);
